"use client"

import { title } from "@/config/primitives"
import { ContentNullWhere } from "@/components/others/content_null"
import { useStoreSession } from "@/app/store/slices/session/hook"
import { Button } from "@heroui/button"
import { IoIosArrowForward } from "react-icons/io"
import { useState } from "react"
import { AiOutlineCheckCircle, AiOutlineDashboard, AiOutlineHome, AiOutlineLogin, AiOutlineUser, AiOutlineVideoCamera } from "react-icons/ai"
import { usePathname, useRouter } from "next/navigation"
import clsx from "clsx"
import { Spacer } from "@heroui/spacer"
import { useStoreHeaderPin } from "@/app/store/slices/other/header_pin/hook"
import { Tooltip } from "@heroui/tooltip"
import { MdExitToApp, MdOutlineCampaign, MdOutlineCast, MdOutlineEditCalendar, MdOutlineExitToApp, MdOutlineManageAccounts, MdOutlineNewspaper, MdOutlinePushPin, MdOutlineSupervisedUserCircle, MdOutlineVerifiedUser, MdOutlineViewTimeline, MdRadio, MdViewStream, MdSettings } from "react-icons/md"
import { ThemeSwitch } from "@/components/others/theme-switch"
import { Accordion, AccordionItem } from "@heroui/accordion"
import { motion, AnimatePresence } from "framer-motion"
import { AvatarImage } from "@/components/others/image"
import { useStorePrivilege } from "@/app/store/slices/collection/privilege/hook"
import { useStoreLive } from "@/app/store/slices/live/hook"
import { Modal, ModalBody, ModalContent, useDisclosure } from "@heroui/modal"
import { Popover, PopoverContent, PopoverTrigger } from "@heroui/popover"
import { ContentNeedSession } from "@/components/others/content_need_session"
import NotificationView from "@/components/others/notification_view"

export function SavaHeader() {
  const { session, signout, self } = useStoreSession()
  const { video, audio } = useStoreLive(true)
  const { privileges_target } = useStorePrivilege(true, true)
  const router = useRouter()
  const { pin, unpin, topin } = useStoreHeaderPin()

  const [expand, setExpand] = useState<boolean>(false)
  const [isHovered, setIsHovered] = useState(false)

  const isExpanded = pin || expand || isHovered;

  const pathname = usePathname()
  const signoutDisclosure = useDisclosure()

  return (
    <ContentNullWhere where={["/", "/auth/signin", "/auth/pass", "/auth/attente"]}>
      <ContentNeedSession>
        <motion.nav
          initial={{ x: -80, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className={clsx("fixed inset-y-0 left-0 z-[50] flex flex-col h-screen transition-all duration-300 ease-in-out border-r border-slate-200/60 bg-white/70 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60 dark:bg-slate-950/70 dark:border-slate-800/60", {
            "w-[280px]": isExpanded,
            "w-[80px]": !isExpanded
          })}
          onMouseEnter={() => !pin && setIsHovered(true)}
          onMouseLeave={() => !pin && setIsHovered(false)}
        >
          <div className={clsx(
            "h-24 flex items-center border-b border-slate-100/50 dark:border-slate-800/50 transition-all duration-300",
            isExpanded ? "justify-between px-4" : "justify-center px-0"
          )}>

            <div className={clsx(
              "flex items-center gap-4",
              !isExpanded ? "w-auto" : "overflow-hidden"
            )}>
              <div className="flex-shrink-0 min-w-[40px] h-[40px] flex items-center justify-center rounded-2xl bg-[#E31D2B] text-white shadow-lg shadow-red-500/20 ring-2 ring-red-100 dark:ring-red-900/30">
                <span className="font-bold text-xl tracking-tighter">S</span>
              </div>

              <AnimatePresence mode="wait">
                {isExpanded && (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col whitespace-nowrap"
                  >
                    <span className={title({ className: "!text-xl font-bold tracking-tight leading-none text-slate-800 dark:text-slate-100" })}>
                      Sava
                    </span>
                    <span className="text-[10px] text-[#E31D2B] font-bold uppercase tracking-[0.2em]">
                      Radio & TV
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {isExpanded && (
              <Tooltip content={pin ? "Détacher" : "Épingler le menu"}>
                <Button
                  isIconOnly
                  variant="light"
                  size="sm"
                  className={clsx(
                    "text-slate-400 hover:text-[#E31D2B] transition-colors",
                    { "bg-[#E31D2B]/10 text-[#E31D2B]": pin }
                  )}
                  onPress={() => pin ? unpin() : topin()}
                >
                  <MdOutlinePushPin className={pin ? "rotate-45" : ""} size={18} />
                </Button>
              </Tooltip>
            )}
          </div>

          <ScrollableNavContent>
            <div className="flex flex-col gap-1.5 px-3 py-6">
              <NavItem icon={<AiOutlineHome size={22} />} label="Accueil" target="/home" active={pathname === "/home"} expanded={isExpanded} router={router} />
              <NavItem icon={<AiOutlineDashboard size={22} />} label="Tableau de bord" target="/dashboard" active={pathname === "/dashboard"} expanded={isExpanded} router={router} />

              <Spacer y={4} />
              {isExpanded && <SectionTitle label="Gestion" />}

              <NavAccordion
                icon={<MdOutlineEditCalendar size={22} />}
                label="Planification"
                pathPrefix="/planification"
                pathname={pathname}
                expanded={isExpanded}
              >
                <SubNavItem label="Programme" target="/planification/schedule" current={pathname} router={router} icon={<MdOutlineViewTimeline />} />
                {(session?.role === "admin" || privileges_target?.some(item => item.validated === true && [3, 4].includes(+item.type))) && (
                  <SubNavItem label="Publicité" target="/planification/advertise" current={pathname} router={router} icon={<MdOutlineCampaign />} />
                )}
              </NavAccordion>

              <NavItem icon={<AiOutlineCheckCircle size={22} />} label="Participation" target="/participation" active={pathname.startsWith("/participation")} expanded={isExpanded} router={router} />

              {/* ACCORDION ADMINISTRATION */}
              {(session?.role === "admin" || privileges_target?.some(item => [2, 3, 4, 6, 7].includes(+item.type) && !!item.validated)) && (
                <NavAccordion
                  icon={<MdOutlineManageAccounts size={22} />}
                  label="Administration"
                  pathPrefix="/manage"
                  pathname={pathname}
                  expanded={isExpanded}
                >
                  {session?.role === "admin" && <SubNavItem label="Administrateurs" target="/manage/admin" current={pathname} router={router} icon={<MdOutlineSupervisedUserCircle />} />}
                  {(session?.role === "admin" || privileges_target?.some(item => [2, 4].includes(+item.type))) &&
                    <SubNavItem label="Opérateurs" target="/manage/operator" current={pathname} router={router} icon={<MdOutlineVerifiedUser />} />
                  }
                  {(session?.role === "admin" || privileges_target?.some(item => [3, 4].includes(+item.type))) &&
                    <SubNavItem label="Clients" target="/manage/client" current={pathname} router={router} icon={<AiOutlineUser />} />
                  }
                  {(session?.role === "admin" || privileges_target?.some(item => [4, 6].includes(+item.type))) &&
                    <SubNavItem label="Actualités" target="/manage/actuality" current={pathname} router={router} icon={<MdOutlineNewspaper />} />
                  }
                  {(session?.role === "admin" || privileges_target?.some(item => [4, 7].includes(+item.type))) &&
                    <SubNavItem label="Pubs & Annonces" target="/manage/advertising" current={pathname} router={router} icon={<MdOutlineCampaign />} />
                  }
                  {session?.role === "admin" && <SubNavItem label="Flux Streaming" target="/manage/flux" current={pathname} router={router} icon={<MdOutlineCast />} />}
                </NavAccordion>
              )}

              {(session?.role === "admin" || privileges_target?.some(item => [1, 4].includes(+item.type) && item.validated === true)) && (
                <NavItem icon={<AiOutlineVideoCamera size={22} />} label="Studio Live" target="/live" active={pathname.startsWith("/live")} expanded={isExpanded} router={router} isSpecial />
              )}
            </div>
          </ScrollableNavContent>
          <div className="border-t border-slate-200/50 bg-slate-50/50 dark:bg-slate-900/30 dark:border-slate-800/50 p-4">

            <div className={clsx("flex gap-2 mb-4 transition-all justify-center items-center", { "flex-col": !isExpanded, "flex-row": isExpanded })}>
              <LiveStatusButton isActive={audio.audio} icon={<MdRadio />} label="Radio" expanded={isExpanded} onPress={() => audio.toggle("expand")} activeColor="bg-[#E31D2B] text-white shadow-md shadow-red-500/20" />
              <LiveStatusButton isActive={video.video} icon={<MdViewStream />} label="TV" expanded={isExpanded} onPress={() => video.toggle("expand")} activeColor="bg-[#E31D2B] text-white shadow-md shadow-red-500/20" />
            </div>

            {session ? (
              <div className={clsx("flex items-center gap-3 transition-all rounded-xl p-2", { "bg-white hover:bg-slate-100 border border-slate-100 dark:bg-slate-800 dark:border-slate-700 cursor-pointer shadow-sm": isExpanded, "justify-center": !isExpanded })}>
                <Popover placement="right-end" showArrow offset={10}>
                  <PopoverTrigger>
                    <div className="flex items-center gap-3 w-full">
                      <AvatarImage
                        src={self?.avatar || undefined}
                        radius="md" size={isExpanded ? "md" : "sm"} isBordered className="ring-[#E31D2B]"
                      />
                      {isExpanded && (
                        <div className="flex flex-col flex-grow items-start overflow-hidden">
                          <span className="text-sm font-bold truncate w-full text-slate-700 dark:text-slate-200">{self?.username || "Utilisateur"}</span>
                          <span className="text-[10px] text-slate-400 font-semibold uppercase">{session.role}</span>
                        </div>
                      )}
                      {isExpanded && <MdSettings className="text-slate-300 hover:text-[#E31D2B] transition-colors" />}
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="p-1 min-w-[180px]">
                    <Button variant="light" className="w-full justify-start" startContent={<AiOutlineUser />} onPress={() => router.push("/auth/profile")}>
                      Mon Profil
                    </Button>
                    <div className="w-full px-2 py-1"><ThemeSwitch expand={isExpanded} /></div>
                    <NotificationView expand={false} />
                    <Button color="danger" variant="flat" className="w-full justify-start text-[#E31D2B]" startContent={<MdExitToApp />} onPress={signoutDisclosure.onOpen}>
                      Déconnexion
                    </Button>
                  </PopoverContent>
                </Popover>
              </div>
            ) : (
              <Tooltip content="Se connecter" isDisabled={isExpanded} placement="right">
                <Button
                  fullWidth={isExpanded} isIconOnly={!isExpanded}
                  className="bg-[#E31D2B] text-white shadow-lg shadow-red-500/30"
                  onPress={() => router.push("/auth/signin")}
                >
                  {isExpanded ? "Se Connecter" : <AiOutlineLogin size={20} />}
                </Button>
              </Tooltip>
            )}
          </div>

          <LogoutModal isOpen={signoutDisclosure.isOpen} onClose={signoutDisclosure.onClose} onLogout={() => { signout(); router.push("/auth/signin"); }} />

        </motion.nav>
      </ContentNeedSession>
    </ContentNullWhere>
  )
}

function ScrollableNavContent({ children }: { children: React.ReactNode }) {
  return <div className="flex-1 overflow-y-auto scrollbar-hide">{children}</div>;
}

function SectionTitle({ label }: { label: string }) {
  return <p className="px-2 text-[10px] font-bold text-[#E31D2B]/70 uppercase tracking-widest mt-2 mb-1 opacity-90 animate-appearance-in ml-1">{label}</p>;
}

function NavItem({ icon, label, target, active, expanded, router, isSpecial }: any) {
  const activeClass = isSpecial
    ? "bg-slate-900 text-white shadow-md shadow-slate-900/20"
    : "bg-[#E31D2B]/10 text-[#E31D2B]";

  const content = (
    <Button
      variant="light"
      className={clsx("w-full justify-start h-11 transition-all group hover:bg-slate-100 dark:hover:bg-slate-800", {
        "px-0 justify-center": !expanded,
        [activeClass]: active,
        "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200": !active
      })}
      radius="lg"
      isIconOnly={!expanded}
      onPress={() => router.push(target)}
    >
      <div className={clsx("flex items-center", { "gap-3": expanded })}>
        <span className={clsx({ "text-[#E31D2B]": active && !isSpecial, "text-white": active && isSpecial, "group-hover:text-[#E31D2B] transition-colors": !active })}>{icon}</span>
        {expanded && <span className="truncate font-medium">{label}</span>}
        {active && expanded && <motion.div layoutId="active-pill" className={clsx("ml-auto w-1.5 h-1.5 rounded-full", isSpecial ? "bg-red-500" : "bg-[#E31D2B]")} />}
      </div>
    </Button>
  );

  return expanded ? content : <Tooltip content={label} placement="right" color="foreground">{content}</Tooltip>;
}

function NavAccordion({ icon, label, pathPrefix, pathname, expanded, children }: any) {
  const isActive = pathname.startsWith(pathPrefix);

  if (!expanded) {
    return (
      <Tooltip content={label} placement="right" color="foreground">
        <Button
          isIconOnly variant="light"
          className={clsx("w-full h-11 text-slate-500 hover:text-[#E31D2B]", { "bg-[#E31D2B]/10 text-[#E31D2B]": isActive })}
        >
          {icon}
        </Button>
      </Tooltip>
    );
  }

  return (
    <Accordion variant="light" itemClasses={{ trigger: "py-2 px-2 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg group", content: "py-1 pl-2" }} showDivider={false}>
      <AccordionItem
        key="1" aria-label={label}
        indicator={<IoIosArrowForward className={isActive ? "text-[#E31D2B]" : "text-slate-300 group-hover:text-[#E31D2B]"} />}
        title={
          <div className={clsx("flex items-center gap-3 transition-colors", isActive ? "text-[#E31D2B] font-semibold" : "text-slate-600 dark:text-slate-400 group-hover:text-slate-900")}>
            {icon} <span className="text-medium">{label}</span>
          </div>
        }
      >
        <div className="flex flex-col gap-1 border-l border-slate-200 dark:border-slate-800 ml-2 pl-2">
          {children}
        </div>
      </AccordionItem>
    </Accordion>
  );
}

function SubNavItem({ label, target, current, router, icon }: any) {
  const isActive = current === target;
  return (
    <Button
      size="sm" variant="light"
      className={clsx("w-full justify-start h-9", isActive ? "text-[#E31D2B] font-medium bg-[#E31D2B]/5" : "text-slate-500 hover:text-slate-700")}
      startContent={icon && <span className={clsx("text-tiny", isActive ? "text-[#E31D2B]" : "opacity-50")}>{icon}</span>}
      onPress={() => router.push(target)}
    >
      {label}
    </Button>
  )
}

function LiveStatusButton({ isActive, icon, label, expanded, onPress, activeColor }: any) {
  return (
    <Tooltip content={expanded ? undefined : label} hidden={expanded} placement="right">
      <Button
        size="sm"
        variant="flat"
        className={clsx("font-medium transition-all border border-transparent", {
          "w-full": expanded,
          "h-9 min-w-9 w-9 p-0 rounded-xl": !expanded,
          [activeColor]: isActive,
          "bg-white border-slate-200 text-slate-400 hover:border-[#E31D2B]/30 hover:text-[#E31D2B] dark:bg-slate-800 dark:border-slate-700": !isActive
        })}
        onPress={onPress}
      >
        <span className={clsx({ "animate-pulse": isActive })}>{icon}</span>
        {expanded && <span>{label}</span>}
        {isActive && expanded && <span className="ml-auto w-2 h-2 rounded-full bg-white animate-ping" />}
      </Button>
    </Tooltip>
  );
}

function LogoutModal({ isOpen, onClose, onLogout }: any) {
  return (
    <Modal
      isOpen={isOpen} onClose={onClose}
      backdrop="blur" hideCloseButton size="sm"
      classNames={{ base: "bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-xl" }}
    >
      <ModalContent>
        <ModalBody className="flex flex-col items-center gap-4 py-8 text-center">
          <div className="p-4 rounded-full bg-red-50 text-[#E31D2B] mb-2 ring-1 ring-red-100">
            <MdOutlineExitToApp size={32} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">Déconnexion</h3>
            <p className="text-sm text-slate-500 mt-1">Êtes-vous sûr de vouloir partir ?</p>
          </div>
          <div className="flex gap-3 w-full mt-2">
            <Button fullWidth variant="flat" onPress={onClose} className="bg-slate-100 text-slate-600">Annuler</Button>
            <Button fullWidth className="bg-[#E31D2B] text-white shadow-lg shadow-red-500/20" onPress={() => { onLogout(); onClose(); }}>Confirmer</Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}